import React, { useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { categories, documentationIndex } from '../../utils/documentationLoader';
import './DocumentationMegaMenu.css';

interface DocumentationMegaMenuProps {
  isOpen: boolean;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
  onClose: () => void;
}

interface CategorySection {
  name: string;
  items: Array<{
    id: string;
    title: string;
    fileName: string;
    description?: string;
  }>;
}

const DocumentationMegaMenu: React.FC<DocumentationMegaMenuProps> = ({
  isOpen,
  onMouseEnter,
  onMouseLeave,
  onClose
}) => {
  const menuRef = useRef<HTMLDivElement>(null);

  // Organize documentation by categories
  const categorySections: CategorySection[] = categories.map(category => ({
    name: category,
    items: documentationIndex.filter(doc => doc.category === category)
  }));

  // Handle escape key
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [isOpen, onClose]);

  // Handle click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen, onClose]);

  const handleLinkClick = () => {
    onClose();
  };


  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          ref={menuRef}
          className="documentation-mega-menu"
          initial={{ opacity: 0, y: -10, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: -10, scale: 0.95 }}
          transition={{ duration: 0.2, ease: "easeOut" }}
          onMouseEnter={onMouseEnter}
          onMouseLeave={onMouseLeave}
        >
          <div className="mega-menu-backdrop" />
          <div className="mega-menu-content">
            <div className="mega-menu-container">
              <div className="mega-menu-header">
                <h3 className="mega-menu-title">Documentation</h3>
                <p className="mega-menu-subtitle">
                  Comprehensive guides and technical documentation for USD.P
                </p>
              </div>

              <div className="mega-menu-grid">
                {categorySections.map((section, categoryIndex) => (
                  <motion.div
                    key={section.name}
                    className="mega-menu-category"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{
                      duration: 0.3,
                      delay: categoryIndex * 0.1,
                      ease: "easeOut"
                    }}
                  >
                    <h4 className="category-title">{section.name}</h4>
                    <ul className="category-items">
                      {section.items.map((item, itemIndex) => (
                        <motion.li
                          key={item.id}
                          className="category-item"
                          initial={{ opacity: 0, x: -10 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{
                            duration: 0.2,
                            delay: (categoryIndex * 0.1) + (itemIndex * 0.05),
                            ease: "easeOut"
                          }}
                        >
                          <Link
                            to={`/docs/${item.id}`}
                            className="item-link"
                            onClick={handleLinkClick}
                          >
                            <span className="item-title">{item.title}</span>
                            {item.description && (
                              <span className="item-description">{item.description}</span>
                            )}
                          </Link>
                        </motion.li>
                      ))}
                    </ul>
                  </motion.div>
                ))}
              </div>

              <div className="mega-menu-footer">
                <Link
                  to="/docs"
                  className="view-all-link"
                  onClick={handleLinkClick}
                >
                  <span>View All Documentation</span>
                  <svg 
                    width="16" 
                    height="16" 
                    viewBox="0 0 16 16" 
                    fill="none"
                    className="arrow-icon"
                  >
                    <path 
                      d="M6 12L10 8L6 4" 
                      stroke="currentColor" 
                      strokeWidth="2" 
                      strokeLinecap="round" 
                      strokeLinejoin="round"
                    />
                  </svg>
                </Link>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default DocumentationMegaMenu;